/**
 * Slot glyphs — a small drawing for each of the eleven attachment slots.
 *
 * ## Why slots and not parts
 *
 * There are sixty-three ordinary attachments plus twenty-three mastery parts, and
 * none of them exists as an object that can be photographed. The model layer applies
 * attachments to the chassis as *specifications* — a barrel is a set of numbers that
 * change how the receiver is lathed, not a mesh that gets bolted on — so there is
 * nothing to hand `IconForge`. Making eighty-six of them individually buildable
 * would mean restructuring a 7,000-line model layer for decoration, which is a large
 * risk for a small reward.
 *
 * A glyph per slot is the honest version of the same idea, and it answers the
 * question a player actually has in that screen: not "what does this part look like"
 * — they are all black polymer — but "which part of the gun am I changing". Eleven
 * drawings covers all eighty-six entries and cannot go stale when a part is retuned.
 *
 * ## Inline SVG
 *
 * No rendering, no cache, no data URLs: these are a dozen lines each and the browser
 * draws them at whatever size they are asked for. They inherit `currentColor`, so a
 * slot that is filled, empty or unavailable is styled by CSS rather than by three
 * copies of the drawing.
 */

const V = (body) =>
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"
     stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${body}</svg>`;

/**
 * Keyed by the ids in `ATTACHMENT_SLOTS`, not by the labels shown beside them —
 * `optic` is displayed as SIGHT and `magazine` as FEED, and keying off the display
 * name would break the moment either label was reworded.
 *
 * Each is drawn side-on and in the same orientation as the weapon in the loadout
 * portrait — muzzle to the left, stock to the right — so the glyph reads as a
 * position on the gun rather than as an abstract symbol.
 */
export const SLOT_GLYPH = {
  /* Optic: a tube over a rail, with a reticle. */
  optic: V('<rect x="6" y="7" width="12" height="6" rx="1.4"/><path d="M4 15h16"/><path d="M12 7v6M9 10h6"/>'),
  /* Muzzle device: ported cylinder at the end of a barrel. */
  muzzle: V('<path d="M14 12h7"/><rect x="3" y="9" width="9" height="6" rx="1.2"/><path d="M5.5 9v6M8 9v6"/>'),
  /* Barrel: a long tube with a gas block. */
  barrel: V('<path d="M3 12h16"/><rect x="9" y="8.5" width="4" height="3.5" rx="0.8"/><path d="M19 10v4"/>'),
  /* Under-rail: a grip hanging below the handguard. */
  underbarrel: V('<path d="M3 8h18"/><path d="M10 8v4a2 2 0 0 0 4 0V8"/><path d="M12 12v6"/>'),
  /* Recoil pad: a plate with compression lines. */
  stockPad: V('<rect x="7" y="4" width="6" height="16" rx="1.4"/><path d="M15 6v12M18 8v8"/>'),
  /* Feed: a magazine. */
  magazine: V('<path d="M8 4h8l-1 5H9z"/><path d="M9 9h6l-1.2 11h-3.6z"/>'),
  /* Grip: the rear hand. */
  rearGrip: V('<path d="M6 5h12"/><path d="M14 5l-3 9a3 3 0 0 1-5 1"/><path d="M8 20h6"/>'),
  /* Stock: a shoulder frame. */
  stock: V('<path d="M3 11h9"/><path d="M12 7h5l4 5-4 5h-5z"/>'),
  /* Illuminator: a beam. */
  laser: V('<rect x="4" y="9" width="7" height="6" rx="1.2"/><path d="M13 12h3M17.5 9.5l2.5-2M17.5 14.5l2.5 2"/>'),
  /* Trigger group: a trigger inside its guard. */
  fireMod: V('<path d="M12 4v7"/><path d="M7 11a5 5 0 0 0 10 0"/><path d="M12 11v5"/>'),
  /* Cheek riser: a raised comb. */
  comb: V('<path d="M3 16h18"/><path d="M6 16V9h9l3 3v4"/>'),
};

/** The fallback, for a slot id this file has not learned about yet. */
const UNKNOWN = V('<circle cx="12" cy="12" r="7"/><path d="M12 15v.01M12 9a2 2 0 0 1 1 3.7"/>');

/** SVG markup for a slot, always something. */
export function slotGlyph(slot) {
  return SLOT_GLYPH[slot] ?? UNKNOWN;
}

/** True when a slot has a drawing of its own rather than the fallback. */
export function hasGlyph(slot) {
  return !!SLOT_GLYPH[slot];
}
